import { Component, OnInit } from '@angular/core';
import { ProductionBoardService } from 'src/app/services/production-board/production-board.service';

export interface CampaignBoardRow {
	campaign_id: number;
	campaign_name: string;
	status: string;
	remaining: number;
	lead_cost: number;
	ordered: number;
	leads_done: number;
	billable?: number;
	replacements?: number;
	days: { [date: string]: number }; // keyed by date YYYY-MM-DD
	isEditingOrdered?: boolean;
	tempOrdered?: number;
	isEditingLeadCost?: boolean;
	tempLeadCost?: number;
}

export interface WeekDay {
	date: string; // YYYY-MM-DD
	label: string; // M/D
	dayName: string; // MON, TUE, …
	key: string; // monday, tuesday, …
}

const DAY_KEYS = ['monday', 'tuesday', 'wednesday', 'thursday', 'friday'];

@Component({
	selector: 'app-campaign-week-board',
	templateUrl: './campaign-week-board.component.html',
	styleUrls: ['./campaign-week-board.component.scss'],
})
export class CampaignWeekBoardComponent implements OnInit {
	campaigns: CampaignBoardRow[] = [];
	weekDays: WeekDay[] = [];
	currentWeek: number;
	currentYear: number;
	loading = false;
	weekLabel = '';

	constructor(private productionBoardService: ProductionBoardService) {}

	ngOnInit(): void {
		const today = new Date();
		this.currentWeek = this.getISOWeek(today);
		this.currentYear = today.getFullYear();
		this.loadWeekData();
	}

	loadWeekData(): void {
		this.loading = true;
		this.weekDays = this.getWeekDays(this.currentWeek, this.currentYear);
		this.weekLabel = this.buildWeekLabel();

		this.productionBoardService.getWeekBoard(`${this.currentWeek}-${this.currentYear}`).subscribe({
			next: (response: any) => {
				const raw: any[] = Array.isArray(response) ? response : response?.body || response?.campaigns || [];

				this.campaigns = raw.map((c) => {
					const days: { [date: string]: number } = {};
					this.weekDays.forEach((d) => {
						days[d.date] = parseFloat(c[d.key] ?? '0') || 0;
					});

					const ordered = Number(c.ordered) || 0;
					const leadsDone = Number(c.leads_done) || 0;

					return {
						campaign_id: c.campaign_id,
						campaign_name: c.campaign_name || `Campaign #${c.campaign_id}`,
						status: c.status || 'Unfilled',
						lead_cost: Number(c.lead_cost) || 0,
						ordered,
						leads_done: leadsDone,
						remaining: ordered - leadsDone,
						billable: Number(c.billable) || 0,
						replacements: Number(c.replacements) || 0,
						days,
					};
				});
				this.loading = false;
			},
			error: () => {
				this.loading = false;
			},
		});
	}

	prevWeek(): void {
		if (this.currentWeek === 1) {
			this.currentYear--;
			this.currentWeek = this.getWeeksInYear(this.currentYear);
		} else {
			this.currentWeek--;
		}
		this.loadWeekData();
	}

	nextWeek(): void {
		const maxWeeks = this.getWeeksInYear(this.currentYear);
		if (this.currentWeek >= maxWeeks) {
			this.currentWeek = 1;
			this.currentYear++;
		} else {
			this.currentWeek++;
		}
		this.loadWeekData();
	}

	saveDayEntry(campaign: CampaignBoardRow, day: WeekDay): void {
		this.productionBoardService
			.updateWeekBoardEntry({
				campaign_id: campaign.campaign_id,
				week_id: `${this.currentWeek}-${this.currentYear}`,
				day: day.key,
				value: campaign.days[day.date],
			})
			.subscribe();
	}

	editOrdered(campaign: CampaignBoardRow): void {
		campaign.isEditingOrdered = true;
		campaign.tempOrdered = campaign.ordered;
	}

	saveOrdered(campaign: CampaignBoardRow): void {
		if (campaign.isEditingOrdered) {
			campaign.isEditingOrdered = false;
			const newValue = campaign.tempOrdered ?? campaign.ordered;

			if (newValue !== campaign.ordered) {
				campaign.ordered = newValue;
				campaign.remaining = campaign.ordered - campaign.leads_done;

				// Here the user asked to send 'campaing_id' but usually it's 'campaign_id'.
				// The backend might be expecting 'campaing_id' or 'campaign_id'. Let's use what the service accepts.
				this.productionBoardService
					.updateLeadsOrdered({
						campaign_id: campaign.campaign_id,
						week_id: `${this.currentWeek}-${this.currentYear}`,
						leads_ordered: campaign.ordered,
					})
					.subscribe();
			}
		}
	}

	editLeadCost(campaign: CampaignBoardRow): void {
		campaign.isEditingLeadCost = true;
		campaign.tempLeadCost = campaign.lead_cost;
	}

	saveLeadCost(campaign: CampaignBoardRow): void {
		if (campaign.isEditingLeadCost) {
			campaign.isEditingLeadCost = false;
			const newValue = campaign.tempLeadCost ?? campaign.lead_cost;

			if (newValue !== campaign.lead_cost) {
				campaign.lead_cost = newValue;

				this.productionBoardService
					.updateLeadCost({
						campaign_id: campaign.campaign_id,
						week_id: `${this.currentWeek}-${this.currentYear}`,
						lead_cost: campaign.lead_cost,
					})
					.subscribe();
			}
		}
	}

	getTotal(campaign: CampaignBoardRow): number {
		return Object.values(campaign.days).reduce((sum, v) => sum + (v ?? 0), 0);
	}

	getRealRevenue(campaign: CampaignBoardRow): number {
		return this.getBillable(campaign) * campaign.lead_cost;
	}

	getBillable(campaign: CampaignBoardRow): number {
		return campaign.leads_done - campaign.replacements;
	}

	getExpectedRevenue(campaign: CampaignBoardRow): number {
		return campaign.leads_done * campaign.lead_cost;
	}

	getCampaignStatus(campaign: CampaignBoardRow): string {
		if (campaign.ordered > 0 && campaign.leads_done >= campaign.ordered) {
			return 'Complete';
		}
		return campaign.status || '';
	}

	getStatusClass(campaign: CampaignBoardRow): string {
		const s = this.getCampaignStatus(campaign).toLowerCase();
		if (s === 'in progress') return 'badge-in-progress';
		if (s === 'unfilled') return 'badge-unfilled';
		if (s === 'complete') return 'badge-complete';
		return 'badge-default';
	}

	private buildWeekLabel(): string {
		if (!this.weekDays.length) return '';
		const [y, m, d] = this.weekDays[0].date.split('-').map(Number);
		const date = new Date(y, m - 1, d);
		return `Week of ${date.toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' })}`;
	}

	private getWeekDays(week: number, year: number): WeekDay[] {
		const monday = this.getISOWeekMonday(week, year);
		const labels = ['MON', 'TUE', 'WED', 'THU', 'FRI'];
		return labels.map((name, i) => {
			const d = new Date(monday);
			d.setDate(monday.getDate() + i);
			const date = d.toISOString().split('T')[0];
			const label = `${d.getMonth() + 1}/${d.getDate()}`;
			return { date, label, dayName: name, key: DAY_KEYS[i] };
		});
	}

	private getISOWeekMonday(week: number, year: number): Date {
		const jan4 = new Date(year, 0, 4);
		const dow = jan4.getDay() || 7;
		const week1Monday = new Date(jan4);
		week1Monday.setDate(jan4.getDate() - (dow - 1));
		const monday = new Date(week1Monday);
		monday.setDate(week1Monday.getDate() + (week - 1) * 7);
		return monday;
	}

	private getISOWeek(date: Date): number {
		const d = new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate()));
		const dayNum = d.getUTCDay() || 7;
		d.setUTCDate(d.getUTCDate() + 4 - dayNum);
		const yearStart = new Date(Date.UTC(d.getUTCFullYear(), 0, 1));
		return Math.ceil(((d.getTime() - yearStart.getTime()) / 86400000 + 1) / 7);
	}

	private getWeeksInYear(year: number): number {
		return this.getISOWeek(new Date(year, 11, 28));
	}

	getDayTotal(day: WeekDay): number {
		return this.campaigns.reduce((sum, c) => sum + (c.days[day.date] ?? 0), 0);
	}

	getDayRevenueTotal(day: WeekDay): number {
		return this.campaigns.reduce((sum, c) => sum + (c.days[day.date] ?? 0) * c.lead_cost, 0);
	}

	getWeekTotal(): number {
		return this.campaigns.reduce((sum, c) => sum + this.getTotal(c), 0);
	}

	getLeadsDoneTotal(): number {
		return this.campaigns.reduce((sum, c) => sum + (c.leads_done ?? 0), 0);
	}

	getBillableTotal(): number {
		return this.campaigns.reduce((sum, c) => sum + this.getBillable(c), 0);
	}

	getReplacementsTotal(): number {
		return this.campaigns.reduce((sum, c) => sum + (c.replacements ?? 0), 0);
	}

	getWeekRevenueTotal(): number {
		return this.campaigns.reduce((sum, c) => sum + this.getRealRevenue(c), 0);
	}

	getWeekExpectedRevenueTotal(): number {
		return this.campaigns.reduce((sum, c) => sum + this.getExpectedRevenue(c), 0);
	}
}
